import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { KVAnnotation, KVMeasurement } from 'types'

/**
 * Extracts the highest number from labels that match a pattern like "A1", "A2", etc.
 */
export function getMaxNumberFromLabels(
  items: (KVAnnotation | KVMeasurement)[] | undefined,
  prefix: string
): number {
  if (!items || items.length === 0) {
    return 0
  }

  const numbers = items
    .map((item) => {
      const match = item.label?.match(new RegExp(`^${prefix}(\\d+)$`))
      return match ? parseInt(match[1], 10) : 0
    })
    .filter((n) => n > 0)

  return numbers.length > 0 ? Math.max(...numbers) : 0
}

/**
 * Generates a default label for an annotation based on the current counter.
 * Treats "New Annotation" as a default that should be replaced.
 */
export function generateAnnotationLabel(
  providedLabel: string | undefined,
  counter: number
): string {
  const trimmedLabel = providedLabel?.trim()
  if (
    trimmedLabel &&
    trimmedLabel !== '' &&
    trimmedLabel !== 'New Annotation'
  ) {
    return trimmedLabel
  }
  return `A${counter}`
}

/**
 * Generates a default label for a measurement based on the current counter.
 */
export function generateMeasurementLabel(
  providedLabel: string | undefined,
  counter: number
): string {
  if (providedLabel && providedLabel.trim() !== '') {
    return providedLabel
  }
  return `M${counter}`
}

/**
 * Increments the annotation counter and returns the current value.
 */
export function incrementAnnotationCounter(
  currentCounter: number,
  setCounter: Dispatch<SetStateAction<number>>
): number {
  const value = currentCounter
  setCounter(currentCounter + 1)
  return value
}

/**
 * Increments the measurement counter and returns the current value.
 */
export function incrementMeasurementCounter(
  currentCounter: number,
  setCounter: Dispatch<SetStateAction<number>>
): number {
  const value = currentCounter
  setCounter(currentCounter + 1)
  return value
}

/**
 * Custom hook to manage annotation and measurement counters.
 * Automatically initializes counters based on existing labels and provides
 * setter functions to increment them.
 */
export function useAnnotationCounters(
  annotations: KVAnnotation[] | undefined,
  measurements: KVMeasurement[] | undefined,
  kestrelModelId: string | undefined
) {
  const [nextAnnotationNumber, setNextAnnotationNumber] = useState(1)
  const [nextMeasurementNumber, setNextMeasurementNumber] = useState(1)

  // Initialize annotation counter based on existing annotations
  useEffect(() => {
    const maxAnnotationNumber = getMaxNumberFromLabels(annotations, 'A')
    setNextAnnotationNumber(maxAnnotationNumber + 1)
  }, [kestrelModelId, annotations])

  // Initialize measurement counter based on existing measurements
  useEffect(() => {
    const maxMeasurementNumber = getMaxNumberFromLabels(measurements, 'M')
    setNextMeasurementNumber(maxMeasurementNumber + 1)
  }, [kestrelModelId, measurements])

  return {
    nextAnnotationNumber,
    setNextAnnotationNumber,
    nextMeasurementNumber,
    setNextMeasurementNumber,
  }
}
